/**
 * ChangeProductStatusTableSelect Component
 *
 * A dropdown selector for changing the status of a product in a table.
 * - Supports variants like "Available", "Unavailable", "Hide", etc.
 * - Uses a portal to render the dropdown outside the normal DOM hierarchy
 * - Handles clicks outside the dropdown to close it
 * - Updates product status via an API mutation
 * - Displays loading and error states
 *
 * @component
 *
 * @param {Object} props
 * @param {Record<string, string>} props.variantEnum - Enum of possible status options
 * @param {string} props.selectedOption - Initial selected option
 * @param {any} props.rowProduct - The product object corresponding to the row
 *
 * @example
 * <ChangeProductStatusTableSelect
 *    variantEnum={{ AVAILABLE: 'Available', UNAVAILABLE: 'Unavailable', HIDE: 'Hide' }}
 *    selectedOption="Available"
 *    rowProduct={product}
 * />
 */

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { TableSelectProps } from "../../types";
import { useChangeProductStauts } from "../../utils/api/Products/useChangeProductStatus";
import ErrorsWrapper from "../GeneralComponents/ErrorsWrapper";
import Loader from "../GeneralComponents/Loader";

const ChangeProductStatusTableSelect: React.FC<TableSelectProps> = ({
  variantEnum,
  selectedOption,
  rowProduct,
}) => {
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => state.auth);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [currentOption, setCurrentOption] = useState(selectedOption || "");

  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  const {
    mutate: ChangeProductStautsMutate,
    status: ChangeProductStautsStatus,
    error: ChangeProductStautsError,
  } = useChangeProductStauts();

  const getVariant = useCallback((value: string) => {
    switch (value) {
      case "Available":
        return "bg-primaryLighter !text-primaryMain";
      case "Unavailable":
        return "bg-warningLighter !text-warningMain";
      case "Hide":
        return "bg-hideOptionColor !text-textSecondary";
      default:
        return "bg-gray-300 text-black";
    }
  }, []);

  const handleOptionClick = (option: string) => {
    setCurrentOption(option);
    ChangeProductStautsMutate(
      {
        payload: {
          productId: rowProduct?.Id,
          StatusPayload: { Status: option },
        },
        token: user?.token,
      },
      {
        onSuccess: (response) => {
          if (response.Status) {
            setCurrentOption(option);
            rowProduct.Status = option;
          }
        },
      },
    );
    setDropdownOpen(false);
  };

  const otherOptions = Object.values(variantEnum);

  // Handle outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  // Sync with prop
  useEffect(() => {
    if (rowProduct?.Status && rowProduct.Status !== currentOption) {
      setCurrentOption(rowProduct.Status);
    }
  }, [rowProduct?.Status]);

  // Calculate dropdown position
  useLayoutEffect(() => {
    if (dropdownOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const estimatedHeight = 160;
      const showAbove = window.innerHeight - rect.bottom < estimatedHeight;

      setDropdownStyle({
        position: "fixed",
        top: showAbove ? rect.top - estimatedHeight - 8 : rect.bottom + 8,
        left: rect.left,
        zIndex: 9999,
      });
    }
  }, [dropdownOpen]);

  const dropdownContent = (
    <div
      ref={dropdownRef}
      style={dropdownStyle}
      className="page-animate z-[9999] w-fit rounded-lg bg-white shadow"
    >
      <div className="clip-triangle absolute -top-2 right-4 h-6 w-6 bg-white"></div>
      {ChangeProductStautsStatus === "pending" ? (
        <div className="flex items-center justify-center p-4">
          <Loader radius={50} />
        </div>
      ) : (
        <ul className="flex w-fit flex-col gap-1 p-2 text-sm">
          {otherOptions.map((option) => {
            const isSelected = option === currentOption;
            return (
              <li
                key={option}
                onClick={() => handleOptionClick(option)}
                className={`cursor-pointer rounded-xl px-4 py-2 ${
                  isSelected ? "font-bold" : ""
                } ${getVariant(option)}`}
              >
                {t(option.charAt(0).toUpperCase() + option.slice(1))}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setDropdownOpen((prev) => !prev)}
        type="button"
        className={`text-white ${getVariant(
          currentOption || "",
        )} inline-flex items-center rounded-lg px-5 py-2 text-sm font-medium`}
      >
        {currentOption
          ? t(currentOption.charAt(0).toUpperCase() + currentOption.slice(1))
          : t("select_status")}
        <svg
          className="ms-3 h-2.5 w-2.5"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 10 6"
        >
          <path
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="m1 1 4 4 4-4"
          />
        </svg>
      </button>

      {dropdownOpen &&
        typeof window !== "undefined" &&
        document.getElementById("portal-root") &&
        ReactDOM.createPortal(
          dropdownContent,
          document.getElementById("portal-root")!,
        )}

      {ChangeProductStautsError ? (
        <ErrorsWrapper message={ChangeProductStautsError.message} />
      ) : null}
    </>
  );
};

export default ChangeProductStatusTableSelect;
